/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import type { PointerEvent, ReactNode } from "react";

import { cssFns } from "@superweb/css";

import { useTypo } from "../typo";
import { Button } from "../buttons/button";
import { LinkButton } from "../buttons/link-button";
import { Spin } from "../spin/spin";

export type PlaceholderAction = {
  label: string;
  onPress?: () => void;
};

export type PlaceholderLink = {
  label: string;
  onPress?: () => void;
  link: {
    href: string;
    onClick?: (e: PointerEvent<HTMLAnchorElement>) => void;
    external?: boolean;
  };
};

const images = {
  waitingMan: {
    src: new URL("images/waiting-man.svg", import.meta.url).href,
    width: 378,
    height: 281,
  },
};

export const Placeholder = ({
  title,
  description,
  actions,
  links,
  progress,
  image,
}: {
  title?: string;
  description?: string;
  actions?: PlaceholderAction[];
  links?: PlaceholderLink[];
  progress?: boolean;
  image?: ReactNode;
}) => {
  const typo = useTypo();

  return (
    <div
      css={{
        ...cssFns.margin("0", "auto"),
        ...cssFns.placeContent("center"),
        display: "grid",
        flexGrow: "1",
        gridColumnStart: "1",
        ...cssFns.margin("0", "auto"),
        ...cssFns.placeContent("center"),
        paddingBlockStart: "32px",
      }}
    >
      <div
        css={{
          ...cssFns.padding("16px"),
          display: "grid",
          justifyContent: "center",
          maxWidth: "394px",
          textAlign: "center",
          boxSizing: "border-box",
          rowGap: "30px",
          paddingInlineStart: "32px",
          paddingInlineEnd: "32px",
        }}
      >
        {progress ? (
          <Spin />
        ) : (
          <>
            <div>
              {image !== undefined ? (
                <div
                  aria-hidden="true"
                  css={{
                    height: "280px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {image}
                </div>
              ) : (
                <img
                  src={images.waitingMan.src}
                  height={images.waitingMan.height}
                  width={images.waitingMan.width}
                  css={{
                    width: "auto",
                    height: "auto",
                    maxHeight: "100%",
                    maxWidth: "100%",
                  }}
                />
              )}
              <h2
                css={{
                  ...typo({
                    level: "title4",
                    weight: "medium",
                    density: "normal",
                  }),
                  ...cssFns.margin("0"),
                }}
              >
                {title}
              </h2>
              <span
                css={typo({
                  level: "body2",
                  density: "normal",
                  weight: "regular",
                })}
              >
                {description}
              </span>
            </div>
            {actions?.length || links?.length ? (
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "12px",
                  alignItems: "center",
                }}
              >
                {actions?.length ? (
                  <div
                    css={{
                      display: "flex",
                      columnGap: "12px",
                    }}
                  >
                    {actions.map((action, i) => (
                      <div
                        key={i}
                        css={{
                          display: "grid",
                          minWidth: "180px",
                          justifyContent: "normal",
                        }}
                      >
                        <Button
                          text={action.label}
                          view="action"
                          onPress={action.onPress}
                        />
                      </div>
                    ))}
                  </div>
                ) : undefined}
                {links?.length ? (
                  <div
                    css={{
                      display: "flex",
                      columnGap: "12px",
                    }}
                  >
                    {links.map((link, i) => (
                      <div
                        key={i}
                        css={{
                          justifyContent: "normal",
                          display: "grid",
                          minWidth: "180px",
                        }}
                      >
                        <LinkButton
                          view="ghost"
                          size="m"
                          link={link.link}
                          text={link.label}
                          onPress={link.onPress}
                        />
                      </div>
                    ))}
                  </div>
                ) : undefined}
              </div>
            ) : undefined}
          </>
        )}
      </div>
    </div>
  );
};
